// src/AuthContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { supabase } from "./supabaseClient";

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

/**
 * Errors such as these mean the locally stored Supabase
 * session should no longer be trusted.
 *
 * Examples:
 * - deleted auth.users row
 * - invalid JWT
 * - expired / unusable refresh session
 */
function isDefinitiveAuthError(error) {
  if (!error) return false;

  const status = Number(error?.status || 0);

  const text = [
    error?.name,
    error?.message,
    error?.code,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    status === 401 ||
    status === 403 ||
    status === 404
  ) {
    return true;
  }

  return (
    text.includes("user from sub claim") ||
    text.includes("user not found") ||
    text.includes("invalid jwt") ||
    text.includes("jwt expired") ||
    text.includes("invalid refresh token") ||
    text.includes("refresh token not found")
  );
}

/**
 * Clear only this browser's Supabase Auth session.
 *
 * Using scope:"local" means we are not deliberately
 * signing the user's other devices out.
 */
async function clearLocalSupabaseSession() {
  try {
    const { error } =
      await supabase.auth.signOut({
        scope: "local",
      });

    if (error) {
      console.warn(
        "AuthContext local signOut warning:",
        error
      );
    }
  } catch (error) {
    console.warn(
      "AuthContext local signOut failed:",
      error
    );
  }
}

export const AuthProvider = ({
  children,
}) => {
  const [session, setSession] =
    useState(null);

  const [user, setUser] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {
    let alive = true;

    /**
     * Each auth event gets a request number.
     *
     * This prevents an older async getUser()
     * request from overwriting a newer auth state.
     */
    let validationRequest = 0;

    const scheduledValidations =
      new Set();

    const applyLoggedOutState = () => {
      if (!alive) return;

      setSession(null);
      setUser(null);
      setLoading(false);
    };

    /**
     * Do not trust candidateSession.user by itself.
     *
     * getSession() can return a locally cached JWT/session
     * even when the corresponding auth.users row has since
     * been deleted.
     *
     * getUser() asks Supabase Auth to validate the current
     * authenticated identity.
     */
    const validateAndApplySession =
      async (
        candidateSession,
        requestId,
        source = "unknown"
      ) => {
        if (!alive) return;

        if (!candidateSession) {
          if (
            requestId !==
            validationRequest
          ) {
            return;
          }

          applyLoggedOutState();
          return;
        }

        try {
          const {
            data,
            error,
          } =
            await supabase.auth.getUser();

          if (
            !alive ||
            requestId !==
              validationRequest
          ) {
            return;
          }

          const validatedUser =
            data?.user ?? null;

          const candidateUserId =
            candidateSession?.user?.id ??
            null;

          const validatedUserId =
            validatedUser?.id ?? null;

          const identityMismatch =
            Boolean(
              candidateUserId &&
                validatedUserId &&
                candidateUserId !==
                  validatedUserId
            );

          if (
            error ||
            !validatedUser ||
            identityMismatch
          ) {
            if (error) {
              console.warn(
                `AuthContext validation failed (${source}):`,
                error
              );
            }

            if (identityMismatch) {
              console.warn(
                "AuthContext session identity mismatch:",
                {
                  candidateUserId,
                  validatedUserId,
                }
              );
            }

            /**
             * Only permanently remove the locally persisted
             * auth session when the error tells us the
             * credentials/user are genuinely invalid.
             *
             * A temporary network failure should not destroy
             * an otherwise valid refresh session.
             */
            if (
              identityMismatch ||
              isDefinitiveAuthError(
                error
              )
            ) {
              await clearLocalSupabaseSession();

              /**
               * signOut() may trigger SIGNED_OUT,
               * which increments validationRequest.
               *
               * If that happened, the newer event owns
               * the state update.
               */
              if (
                !alive ||
                requestId !==
                  validationRequest
              ) {
                return;
              }
            }

            applyLoggedOutState();
            return;
          }

          /**
           * Use the server-validated user rather than
           * candidateSession.user.
           */
          setSession({
            ...candidateSession,
            user: validatedUser,
          });

          setUser(validatedUser);
          setLoading(false);
        } catch (error) {
          if (
            !alive ||
            requestId !==
              validationRequest
          ) {
            return;
          }

          console.error(
            `AuthContext getUser validation error (${source}):`,
            error
          );

          if (
            isDefinitiveAuthError(
              error
            )
          ) {
            await clearLocalSupabaseSession();

            if (
              !alive ||
              requestId !==
                validationRequest
            ) {
              return;
            }
          }

          applyLoggedOutState();
        }
      };

    /**
     * Schedule validation outside the direct
     * onAuthStateChange callback.
     *
     * This avoids doing another Supabase Auth request
     * directly inside Supabase's auth-state callback.
     */
    const scheduleValidation = (
      candidateSession,
      source
    ) => {
      const requestId =
        ++validationRequest;

      const timer =
        window.setTimeout(() => {
          scheduledValidations.delete(
            timer
          );

          validateAndApplySession(
            candidateSession,
            requestId,
            source
          );
        }, 0);

      scheduledValidations.add(timer);
    };

    /**
     * Initial bootstrap.
     *
     * getSession() is only used to obtain the candidate
     * session. Its embedded user is NOT exposed until
     * getUser() verifies it.
     */
    (async () => {
      const requestId =
        ++validationRequest;

      try {
        const {
          data,
          error,
        } =
          await supabase.auth.getSession();

        if (!alive) return;

        if (error) {
          console.error(
            "AuthContext getSession error:",
            error
          );

          if (
            isDefinitiveAuthError(
              error
            )
          ) {
            await clearLocalSupabaseSession();

            if (
              !alive ||
              requestId !==
                validationRequest
            ) {
              return;
            }
          }

          applyLoggedOutState();
          return;
        }

        await validateAndApplySession(
          data?.session ?? null,
          requestId,
          "initial-session"
        );
      } catch (error) {
        if (
          !alive ||
          requestId !==
            validationRequest
        ) {
          return;
        }

        console.error(
          "AuthContext initial auth check error:",
          error
        );

        if (
          isDefinitiveAuthError(
            error
          )
        ) {
          await clearLocalSupabaseSession();

          if (
            !alive ||
            requestId !==
              validationRequest
          ) {
            return;
          }
        }

        applyLoggedOutState();
      }
    })();

    const {
      data: {
        subscription,
      },
    } =
      supabase.auth.onAuthStateChange(
        (
          event,
          newSession
        ) => {
          if (!alive) return;

          /**
           * A genuine signed-out event can immediately
           * clear application state.
           */
          if (!newSession) {
            validationRequest += 1;
            applyLoggedOutState();
            return;
          }

          /**
           * During login / initial restoration, auth guards
           * should wait for getUser() validation.
           */
          if (
            event ===
              "INITIAL_SESSION" ||
            event === "SIGNED_IN" ||
            event ===
              "PASSWORD_RECOVERY"
          ) {
            setLoading(true);
          }

          scheduleValidation(
            newSession,
            event
          );
        }
      );

    return () => {
      alive = false;

      validationRequest += 1;

      scheduledValidations.forEach(
        (timer) => {
          window.clearTimeout(timer);
        }
      );

      scheduledValidations.clear();

      subscription?.unsubscribe?.();
    };
  }, []);

  const signOut = async () => {
    try {
      const { error } =
        await supabase.auth.signOut();

      if (error) {
        console.error(
          "AuthContext signOut error:",
          error
        );
      }
    } catch (error) {
      console.error(
        "AuthContext signOut failed:",
        error
      );
    } finally {
      /**
       * Never leave stale application state behind,
       * even if the remote sign-out request itself fails.
       */
      setSession(null);
      setUser(null);
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      signOut,
    }),
    [
      user,
      session,
      loading,
    ]
  );

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () =>
  useContext(AuthContext);